import { StateInterface } from "@/interface/customer/customers.interface";
import { Customer } from "@/modules/customer";
import ENDPOINTS from "@/providers/end-points";
import axios from "@/providers/requests";
import request from "@/providers/requests";
import { CustomerSummary } from "@/modules/customer/summary";
import { CustomerInsights } from "@/modules/customer/insights";
import { MODES } from "@/enums/apps.enum";
import { SummaryCustomerTransaction } from "@/modules/customer/transactions/summary";
import { CustomerTransaction } from "@/modules/customer/transactions/transaction";
import { Response } from "@/interface/response.interface";
import { Translate as $t } from "@/plugins/translate";
import { CustomerTransactionInterface } from "@/interface/customer/transaction";

const state: StateInterface = {
  list: [],
  totalRows: 0,
  customer: null,
  success: false,
  message: "",
  insights: null,
  insightsList: null,
  transactions: [],
  transaction: null,
  isCustomerInsightTab: false,
  isFetching: false,
};

export default {
  namespaced: true,
  state,

  getters: {
    list: (state: any): Array<any> => state.list,
    isFetching: (state: any): Boolean => state.isFetching,
    totalRows: (state: any): number => state.totalRows,
    success: (state: any): boolean => state.success,
    message: (state: any): string => state.message,
    customer: (state: any): any => state.customer,
    Insights: (state: any): any => state.insights,
    InsightsList: (state: any): any => state.insightsList,
    Transactions: (state: any): Array<any> => state.transactions,
    Transaction: (state: any): CustomerTransactionInterface => state.transaction,
    IsCustomerInsightTab: (state: any): boolean => state.isCustomerInsightTab,
  },

  mutations: {
    SET_CUSTOMERS_LIST_FETCHING(state: any, isFetching = false) {
      state.isFetching = isFetching;
    },

    SET_CUSTOMERS_LIST(state: any, payload) {
      state.list = payload.data;
      state.totalRows = payload.count;
    },

    SET_CUSTOMERS_CREATED(state: any, payload) {
      state.success = payload.success;
      state.message = payload.message || "";
    },

    SET_CUSTOMERS_UPDATED(state: any, payload) {
      state.success = payload.success;
      state.message = payload.message || "";
    },

    SET_CUSTOMER(state: any, payload = null) {
      state.customer = payload;
    },

    SET_CUSTOMER_INSIGHTS(state: any, payload) {
      state.insights = payload;
    },

    SET_IS_CUSTOMER_INSIGHTS_TAB(state: any, boolean) {
      state.isCustomerInsightTab = boolean;
    },

    SET_CUSTOMER_INSIGHTS_LIST(state: any, payload) {
      state.insightsList = payload;
    },

    SET_CUSTOMER_TRANSACTIONS(state: any, transactions) {
      state.transactions = transactions;
    },

    SET_CUSTOMER_TRANSACTION(state: any, transaction) {
      state.transaction = transaction;
    },
  },
  actions: {
    async getCustomers({ commit }: { commit: any }, payload) {
      const { page = 1, mode = MODES.SUMMARY, itemsPerPage = 32, search = null } = payload;

      await commit("SET_CUSTOMERS_LIST_FETCHING", true);

      return await axios
        .get(`${ENDPOINTS.CUSTOMERS_LIST}?page=${page}&mode=${mode}&page_size=${itemsPerPage}&search=${search}`)
        .then((response) => response.data)
        .then(({ data, count }) => ({
          data: data.map((item) => (mode !== MODES.FULL ? new CustomerSummary(item) : new Customer(item))),
          count: count,
        }))
        .then(async ({ data, count }) => {
          await commit("SET_CUSTOMERS_LIST", { data, count });
        })
        .catch(async (error) => {
          await console.log(error);
        })
        .finally(async () => {
          await commit("SET_CUSTOMERS_LIST_FETCHING", false);
        });
    },

    async createCustomer({ commit }: { commit: any }, payload) {
      return await axios
        .post(ENDPOINTS.CUSTOMERS, payload)
        .then((response) => response.data)
        .then(async (response) => {
          await commit("SET_CUSTOMERS_CREATED", { success: true });
          await commit("SET_CUSTOMER", response.data);
        })
        .catch(async ({ error }) => {
          await commit("SET_CUSTOMERS_CREATED", {
            success: false,
            message: error.message || $t("data_not_saved_yet"),
          });
          await console.log(error);
        });
    },

    async updateCustomer({ commit }: { commit: any }, payload) {
      return await axios
        .put(`${ENDPOINTS.CUSTOMERS}/${payload.id}`, payload)
        .then((response) => response.data)
        .then(async (response) => {
          await commit("SET_CUSTOMERS_UPDATED", { success: true });
        })
        .catch(async ({ error }) => {
          await commit("SET_CUSTOMERS_UPDATED", { success: false, message: error?.message });
          await console.log(error);
        });
    },

    async getCustomer({ commit }: { commit: any }, id) {
      return await axios
        .get(ENDPOINTS.CUSTOMER(id))
        .then((response) => response.data)
        .then(async ({ data }) => {
          const customer: Object = new Customer(data);
          await commit("SET_CUSTOMER", customer);
        })
        .catch(async (error) => {
          await commit("SET_CUSTOMER", null);
          await console.log(error);
        });
    },

    async getInsights({ commit }: { commit: any }, { id, location_id }) {
      commit("SET_IS_CUSTOMER_INSIGHTS_TAB", false);
      return await axios
        .get(ENDPOINTS.CUSTOMER_INSIGHTS(id, location_id))
        .then((response) => response.data)
        .then(async ({ data }) => {
          await commit("SET_CUSTOMER_INSIGHTS", new CustomerInsights(data));
        })
        .catch(async (error) => {
          await commit("SET_CUSTOMER_INSIGHTS", new CustomerInsights());
          await console.log(error);
        });
    },

    async getInsightsList({ commit }: { commit: any }, { id, location_id }) {
      return await axios
        .get(ENDPOINTS.CUSTOMER_INSIGHTS(id, location_id))
        .then((response) => response.data)
        .then(async ({ data }) => {
          await commit("SET_CUSTOMER_INSIGHTS_LIST", new CustomerInsights(data));
        })
        .catch(async (error) => {
          await commit("SET_CUSTOMER_INSIGHTS_LIST", new CustomerInsights());
        });
    },

    async getTransactions({ commit }: { commit: any }, { location_ids, begin, end, customer_id }) {
      return await axios
        .get(ENDPOINTS.SALE_AGGREGATES(location_ids, begin, end, customer_id))
        .then((response) => response.data)
        .then(async ({ data }) => {
          const list = data.map((transaction) => new SummaryCustomerTransaction(transaction));
          await commit("SET_CUSTOMER_TRANSACTIONS", list);
        })
        .catch(async (error) => {
          await console.log(error);
          await commit("SET_CUSTOMER_TRANSACTIONS", []);
        });
    },

    async getTransaction({ commit }: { commit: any }, id) {
      return await axios
        .get(ENDPOINTS.SALE(id))
        .then((response) => response.data)
        .then(async ({ data }) => {
          const transaction = new CustomerTransaction(data);
          await commit("SET_CUSTOMER_TRANSACTION", transaction);
          debugger;
        })
        .catch(async (error) => {
          await console.log(error);
          await commit("SET_CUSTOMER_TRANSACTION", {});
        });
    },

    async bulkDeleteCustomers({ commit }: { commit: any }, payload) {
      return await request
        .delete<Response<any>>(`${ENDPOINTS.CUSTOMERS}/batch`, { data: payload })
        .then((response) => response.data)
        .then(async () => {
          await commit("SET_CUSTOMERS_UPDATED", { success: true });
        })
        .catch(async (error) => {
          await commit("SET_CUSTOMERS_UPDATED", { success: false, message: error?.error?.message });
          console.log(error);
        });
    },
  },
};
